import { logoutMember } from '@/utils';
import CommonServices from '@/services/Common';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { mapGetters, mapMutations } from 'vuex';
import { fetchSessionObject, saveSessionObject} from '@/utils/SessionStorageUtils';
import { STORAGE_KEYS } from '@general/js/utils/SessionStorageUtils';

export default {
  data() {
    return {
      inActiveCounter: null,
      autoLogoutCounter: null,
      debounceCounter: null,
      refreshLoginCookieCounter: null,
      userLoggedin: false,
      showInactiveWarningPopup: false,
      showUnderMaintenanceWarningPopup: false,
      maintenanceAlertText: '',
      contentsLoadedFailed: false,
      debounceTime: 2 * 1000, // 2s (2000ms) of off time
      timeToWarnInactive: 7 * 60 * 1000, // 7mins (420000ms) to show popup
      timeToLogout: 3 * 60 * 1000, // 3mins (180000ms) to auto log out once inactive warning popup shown
      refreshLoginCookieTimeout: 60 * 1000, // 1min (60000ms)
    };
  },

  computed: {
    ...mapGetters({
      memberDetails: VUEX_GENERAL.GETTERS.getCurrentMemberDetails,
      policyNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      currentIsExtrasFlag: VUEX_GENERAL.GETTERS.getCurrentPolicyIsExtrasFlag,
      currentExtrasOnlyFlag: VUEX_GENERAL.GETTERS.getCurrentPolicyExtrasOnlyFlag,
    }),
  },

  async beforeMount() {
    try {
      // this also used as to check if user is logged in or not
      const status = await CommonServices.getRefreshCookie();
      if (status.underMaintenance){
        this.showUnderMaintenanceWarningPopup = true;
        this.maintenanceAlertText = status.maintenanceAlertText;
      }
      this.userLoggedin = true;
    } catch (err) {
      logger.log(err);
      // save current url if from email quote before redirecting to login page
      this.saveEmailQuoteUrlIfExists();
      // redirect to signin page. TODO: this would be nice if url coming from BE
      document.getElementById('loginBtn').submit();
    }
  },

  mounted() {
    this.getNavigationAndMemberDetails();

    if (!this.isInDev) {
      // auto logout
      // on member portal load, start counter
      this.userInactiveTimer();

      // on following event listener, reset counter and start again
      window.addEventListener('mousedown', this.sessionTimeoutDebounce);
      window.addEventListener('keydown', this.sessionTimeoutDebounce);
      window.addEventListener('mousemove', this.sessionTimeoutDebounce);
      window.addEventListener('scroll', this.sessionTimeoutDebounce);
      window.addEventListener('touchend', this.sessionTimeoutDebounce); // use 'touchend' to cover all touch action
    }
  },

  beforeUnmount() {
    /**
     * before destroy the app, clear all even listener and timers
     * technically, this may not be executed on page reload/navigate away to other page. browser by default will remove all event listeners and clear all timeouts but keeping this just in case for whatever reason this component get dismounted.
     */
    window.removeEventListener('mousedown', this.sessionTimeoutDebounce);
    window.removeEventListener('keydown', this.sessionTimeoutDebounce);
    window.removeEventListener('mousemove', this.sessionTimeoutDebounce);
    window.removeEventListener('scroll', this.sessionTimeoutDebounce);
    window.removeEventListener('resize', this.sessionTimeoutDebounce);
    window.removeEventListener('touchend', this.sessionTimeoutDebounce);

    clearTimeout(this.inActiveCounter);
    clearTimeout(this.autoLogoutCounter);
    clearTimeout(this.debounceCounter);
    clearTimeout(this.refreshLoginCookieCounter);
  },

  methods: {
    ...mapMutations([
      VUEX_GENERAL.MUTATIONS.updateCurrentMemberDetails,
      VUEX_GENERAL.MUTATIONS.updateLogout,
      VUEX_GENERAL.MUTATIONS.saveThisPolicyNumber,
      VUEX_GENERAL.MUTATIONS.saveThisMembership,
    ]),

    saveEmailQuoteUrlIfExists() {
      const currentUrl = window.location.href;
      const targetSubstring = '/my-cover/change-my-cover/change-my-product/product-selection?quoteId';
      if (currentUrl.includes(targetSubstring)) {
        if (!fetchSessionObject(STORAGE_KEYS.EMAIL_QUOTE_REDIRECT_LINK)) {
          saveSessionObject(currentUrl, STORAGE_KEYS.EMAIL_QUOTE_REDIRECT_LINK);
        }
      }
    },

    sessionTimeoutDebounce() {
      // this only takes effect if debounceCounter is falsy and while initial apis are successed and inactive warning pop up is not up
      if (!this.debounceCounter && !this.showInactiveWarningPopup && this.contentsLoaded && !this.contentsLoadedFailed) {
        clearTimeout(this.inActiveCounter);
        logger.log(`clear session timeout timer (debounce ${Math.floor(this.debounceTime / 1000).toFixed(0)} seconds)`);

        this.debounceCounter = setTimeout(() => {
          this.userInactiveTimer();
          this.callRefreshLoginCookie();

          clearTimeout(this.debounceCounter);
          this.debounceCounter = null;
        }, this.debounceTime);
      }
    },

    userInactiveTimer() {
      logger.log(`session timeout in ${this.convertMillisecondsToSeconds(this.timeToWarnInactive)} mins.`);

      this.inActiveCounter = setTimeout(() => {
        this.showInactiveWarningPopup = true;
        this.autoLogout();
      }, this.timeToWarnInactive);
    },

    logout(){
      this.autoLogoutCounter = setTimeout(() => {
        this.updateLogout(true);
        if (this.memberDetails.memberItems && this.memberDetails.memberItems.logout) {
          logoutMember(this.memberDetails.memberItems.logout, true);
        }
      });
    },

    autoLogout() {
      logger.log(`auto logout in ${this.convertMillisecondsToSeconds(this.timeToLogout)} mins`);
      this.autoLogoutCounter = setTimeout(() => {
        this.updateLogout(true);
        if (this.memberDetails.memberItems && this.memberDetails.memberItems.logout) {
          logoutMember(this.memberDetails.memberItems.logout, true);
        }
      }, this.timeToLogout);
    },

    sessionContinue() {
      clearTimeout(this.autoLogoutCounter);
      logger.log('auto logout canceled');
      this.showInactiveWarningPopup = false;
      this.userInactiveTimer();
    },

    convertMillisecondsToSeconds(ms) {
      return Math.floor(ms / 60000).toFixed(0);
    },

    /**
     * callRefreshLoginCooke() will be called only this was called over 1 min ago.
     */
    callRefreshLoginCookie() {
      if (!this.refreshLoginCookieCounter) {
        this.refreshLoginCookieCounter = setTimeout(async () => {
          const status = await CommonServices.getRefreshCookie();
          if (status.underMaintenance){
            this.showUnderMaintenanceWarningPopup = true;
            this.maintenanceAlertText = status.maintenanceAlertText;
          }
          clearTimeout(this.refreshLoginCookieCounter);
          this.refreshLoginCookieCounter = null;
        }, this.refreshLoginCookieTimeout);
      }
    },
  },
};
